export type LegalRuntimeConfig = {
  companyName?: string;
  businessAddress?: string;
  supportEmail?: string;
  supportPhone?: string;
  siteName?: string;
  ageGateEnabled?: boolean;
  coppaMode?: boolean;
  privacyEffectiveDate?: string;
  termsEffectiveDate?: string;
  cookieEffectiveDate?: string;
  dmcaAgentName?: string;
  dmcaAgentEmail?: string;
  dmcaAgentAddress?: string;
  analyticsProvider?: string;
};

export type LegalConfig = {
  siteName: string;
  companyName: string;
  businessAddress: string;
  supportEmail: string;
  supportPhone: string;
  ageGateEnabled: boolean;
  coppaMode: boolean;
  privacyEffectiveDate: string;
  termsEffectiveDate: string;
  cookieEffectiveDate: string;
  dmcaAgentName: string;
  dmcaAgentEmail: string;
  dmcaAgentAddress: string;
  analyticsProvider: string;
};

export type LegalPlaceholderMap = Record<string, string>;

export type LegalRouteDefinition = {
  path: string;
  data: {
    title: string;
    markdown: string;
    showCookiePreferences?: boolean;
    showSoundCredits?: boolean;
    showOperatorInfo?: boolean;
  };
};

type BuildLegalConfigOptions = {
  environment?: Partial<LegalRuntimeConfig>;
  defaultSiteName: string;
  runtimeConfigKey?: string;
};

const DEFAULT_RUNTIME_CONFIG_KEY = '__NOISY_USER_CONFIG__';

function readRuntimeConfig(runtimeConfigKey: string): Partial<LegalRuntimeConfig> {
  if (typeof window === 'undefined') {
    return {};
  }

  const runtimeConfig = (window as unknown as Record<string, unknown>)[runtimeConfigKey];
  if (!runtimeConfig || typeof runtimeConfig !== 'object') {
    return {};
  }

  return runtimeConfig as Partial<LegalRuntimeConfig>;
}

function resolveLegalValue<T extends keyof LegalRuntimeConfig>(
  key: T,
  environment: Partial<LegalRuntimeConfig>,
  runtimeConfig: Partial<LegalRuntimeConfig>,
  fallback?: LegalRuntimeConfig[T],
): LegalRuntimeConfig[T] | undefined {
  const envValue = environment[key];
  if (envValue !== undefined && envValue !== null && envValue !== '') {
    return envValue;
  }

  const runtimeValue = runtimeConfig[key];
  if (runtimeValue !== undefined && runtimeValue !== null && runtimeValue !== '') {
    return runtimeValue;
  }

  return fallback;
}

export function buildLegalConfig(options: BuildLegalConfigOptions): LegalConfig {
  const environment = options.environment ?? {};
  const runtimeConfig = readRuntimeConfig(options.runtimeConfigKey ?? DEFAULT_RUNTIME_CONFIG_KEY);

  return {
    siteName: String(resolveLegalValue('siteName', environment, runtimeConfig, options.defaultSiteName) ?? options.defaultSiteName),
    companyName: String(resolveLegalValue('companyName', environment, runtimeConfig, '') ?? ''),
    businessAddress: String(resolveLegalValue('businessAddress', environment, runtimeConfig, '') ?? ''),
    supportEmail: String(resolveLegalValue('supportEmail', environment, runtimeConfig, '') ?? ''),
    supportPhone: String(resolveLegalValue('supportPhone', environment, runtimeConfig, '') ?? ''),
    ageGateEnabled: Boolean(resolveLegalValue('ageGateEnabled', environment, runtimeConfig, true) ?? true),
    coppaMode: Boolean(resolveLegalValue('coppaMode', environment, runtimeConfig, false) ?? false),
    privacyEffectiveDate: String(resolveLegalValue('privacyEffectiveDate', environment, runtimeConfig, 'January 1, 2026') ?? 'January 1, 2026'),
    termsEffectiveDate: String(resolveLegalValue('termsEffectiveDate', environment, runtimeConfig, 'January 1, 2026') ?? 'January 1, 2026'),
    cookieEffectiveDate: String(resolveLegalValue('cookieEffectiveDate', environment, runtimeConfig, 'January 1, 2026') ?? 'January 1, 2026'),
    dmcaAgentName: String(resolveLegalValue('dmcaAgentName', environment, runtimeConfig, '[DMCA_AGENT_NAME]') ?? '[DMCA_AGENT_NAME]'),
    dmcaAgentEmail: String(resolveLegalValue('dmcaAgentEmail', environment, runtimeConfig, '[DMCA_AGENT_EMAIL]') ?? '[DMCA_AGENT_EMAIL]'),
    dmcaAgentAddress: String(resolveLegalValue('dmcaAgentAddress', environment, runtimeConfig, '[DMCA_AGENT_ADDRESS]') ?? '[DMCA_AGENT_ADDRESS]'),
    analyticsProvider: String(resolveLegalValue('analyticsProvider', environment, runtimeConfig, '') ?? ''),
  };
}

export function buildLegalPlaceholders(config: LegalConfig): LegalPlaceholderMap {
  return {
    '[SITE_NAME]': config.siteName,
    '[COMPANY_NAME]': config.companyName || config.siteName,
    '[BUSINESS_ADDRESS]': config.businessAddress || 'Not provided',
    '[SUPPORT_EMAIL]': config.supportEmail || 'the Feedback page',
    '[SUPPORT_PHONE]': config.supportPhone || 'Not provided',
    '[PRIVACY_EFFECTIVE_DATE]': config.privacyEffectiveDate,
    '[TERMS_EFFECTIVE_DATE]': config.termsEffectiveDate,
    '[COOKIE_EFFECTIVE_DATE]': config.cookieEffectiveDate,
    '[DMCA_AGENT_NAME]': config.dmcaAgentName,
    '[DMCA_AGENT_EMAIL]': config.dmcaAgentEmail,
    '[DMCA_AGENT_ADDRESS]': config.dmcaAgentAddress,
    '[ANALYTICS_PROVIDER]': config.analyticsProvider || 'Not configured',
  };
}

export function applyLegalPlaceholders(content: string, placeholders: LegalPlaceholderMap): string {
  return Object.entries(placeholders).reduce((accumulator, [token, value]) => {
    return accumulator.split(token).join(value);
  }, content);
}

export const legalRouteDefinitions: LegalRouteDefinition[] = [
  {
    path: 'privacy',
    data: { title: 'Privacy Policy', markdown: 'assets/legal/privacy.md' },
  },
  {
    path: 'terms',
    data: { title: 'Terms & Conditions', markdown: 'assets/legal/terms.md' },
  },
  {
    path: 'cookies',
    data: { title: 'Cookie Policy', markdown: 'assets/legal/cookies.md', showCookiePreferences: true },
  },
  {
    path: 'dmca',
    data: { title: 'DMCA Notice', markdown: 'assets/legal/dmca.md', showSoundCredits: true },
  },
  {
    path: 'accessibility',
    data: { title: 'Accessibility Statement', markdown: 'assets/legal/accessibility.md' },
  },
  {
    path: 'contact',
    data: { title: 'Contact', markdown: 'assets/legal/contact.md', showOperatorInfo: true },
  },
  {
    path: 'feedback',
    data: { title: 'Feedback', markdown: 'assets/legal/feedback.md' },
  },
  {
    path: 'security',
    data: { title: 'Security', markdown: 'assets/legal/security.md' },
  },
];
